/**
 * DROPIT cart extras (snippets/dropit-cart-extras.liquid): one-tap "Leve junto" products and the collectible switch.
 * Uses Shopify.actions.updateCart so Horizon re-renders the drawer / cart page and the cart bubble by itself;
 * falls back to the AJAX cart API plus a reload.
 */

/**
 * @param {Array<{merchandiseId?: string, id?: string, quantity: number}>} lines
 */
async function updateLines(lines) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart({ lines }, { event: { context: 'cart' } });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    return;
  }

  for (const line of lines) {
    const url = line.id ? '/cart/change.js' : '/cart/add.js';
    const body = line.id ? { id: line.id, quantity: line.quantity } : { id: line.merchandiseId, quantity: line.quantity };
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('cart');
  }
  window.location.reload();
}

document.addEventListener('click', async (event) => {
  const button = /** @type {HTMLButtonElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-dp-cart-add]') : null
  );
  if (!button || button.disabled) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    await updateLines([{ merchandiseId: button.dataset.dpCartAdd, quantity: 1 }]);
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.dataset.error = '';
    setTimeout(() => delete button.dataset.error, 1600);
  }
});

document.addEventListener('change', async (event) => {
  const input = /** @type {HTMLInputElement | null} */ (
    event.target instanceof HTMLInputElement && event.target.matches('[data-dp-collectible]') ? event.target : null
  );
  if (!input) return;

  const label = input.closest('.dp-collect');
  label?.classList.toggle('dp-collect--on', input.checked);
  label?.setAttribute('aria-busy', 'true');
  input.disabled = true;

  try {
    if (input.checked) {
      await updateLines([{ merchandiseId: input.dataset.dpCollectible, quantity: 1 }]);
    } else if (input.dataset.lineKey) {
      await updateLines([{ id: input.dataset.lineKey, quantity: 0 }]);
    }
  } catch (error) {
    input.checked = !input.checked;
    label?.classList.toggle('dp-collect--on', input.checked);
  } finally {
    input.disabled = false;
    label?.removeAttribute('aria-busy');
  }
});

/** "Esvaziar" in the cart drawer header: asks inline (Sim / Não, theme style), then removes every line */
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('[data-dp-cart-clear]');
  const yes = target?.closest('[data-dp-clear-yes]');
  const no = target?.closest('[data-dp-clear-no]');

  if (button && !button.hidden) {
    const ask = document.createElement('span');
    ask.className = 'dp-clear-ask';
    ask.setAttribute('role', 'group');
    ask.innerHTML =
      '<span>Esvaziar o carrinho?</span>' +
      '<button type="button" class="dp-clear-ask__yes" data-dp-clear-yes>Sim</button>' +
      '<button type="button" class="dp-clear-ask__no" data-dp-clear-no>Não</button>';
    button.hidden = true;
    button.after(ask);
    ask.querySelector('[data-dp-clear-yes]')?.focus();
    ask.dataset.timer = String(setTimeout(() => { ask.remove(); button.hidden = false; }, 6000));
    return;
  }

  if (no) {
    const ask = no.closest('.dp-clear-ask');
    clearTimeout(Number(ask?.dataset.timer));
    const original = ask?.previousElementSibling;
    ask?.remove();
    if (original instanceof HTMLElement) original.hidden = false;
    return;
  }

  if (yes) {
    const ask = yes.closest('.dp-clear-ask');
    clearTimeout(Number(ask?.dataset.timer));
    yes.textContent = 'Esvaziando…';
    yes.setAttribute('disabled', '');
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (cart.items?.length) await updateLines(cart.items.map((item) => ({ id: item.key, quantity: 0 })));
    } catch (error) {
      await fetch('/cart/clear.js', { method: 'POST' });
      window.location.reload();
    }
  }
});

/**
 * Gift with every order (snippets/dropit-cart-gift.liquid sets window.DropitGift).
 * After every cart change, and once on load: exactly one gift while the cart has any product other than a Supreme
 * (handle "supreme-…"); a cart with only Supreme, or empty, has no gift (removed if there). Stock ends it.
 */
const gift = /** @type {{variant: number, product: number, available: boolean} | undefined} */ (window.DropitGift);
let giftSyncing = false;

/** Theme setting "Vendas encerradas" (window.DropitClosed): those products never stay in the cart */
const closedIds = /** @type {number[]} */ (window.DropitClosed || []);

async function syncGift() {
  if ((!gift?.variant && !closedIds.length) || giftSyncing) return;
  giftSyncing = true;
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const closedLines = (cart.items || []).filter((item) => closedIds.includes(item.product_id));
    if (closedLines.length) {
      await updateLines(closedLines.map((line) => ({ id: line.key, quantity: 0 })));
      return;
    }
    if (!gift?.variant) return;
    const items = cart.items || [];
    const giftLines = items.filter((item) => item.product_id === gift.product);
    const others = items.filter((item) => item.product_id !== gift.product);
    const want = others.some((item) => !String(item.handle).startsWith('supreme-'));

    if (want && giftLines.length === 0 && gift.available) {
      await updateLines([{ merchandiseId: String(gift.variant), quantity: 1 }]);
    } else if (!want && giftLines.length) {
      await updateLines(giftLines.map((line) => ({ id: line.key, quantity: 0 })));
    } else if (giftLines.length && (giftLines.length > 1 || giftLines[0].quantity !== 1)) {
      await updateLines(
        giftLines.map((line, i) => ({ id: line.key, quantity: i === 0 ? 1 : 0 }))
      );
    }
  } catch (error) {
    // Out of stock or a network hiccup: leave the cart as it is
  } finally {
    giftSyncing = false;
  }
}

if ((gift?.variant || closedIds.length) && !window.DropitGiftReady) {
  window.DropitGiftReady = true;
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const done = /** @type {any} */ (event).promise;
    if (done?.then) done.then(() => syncGift(), () => {});
    else setTimeout(syncGift, 400);
  });
  syncGift();
}

/**
 * Free shipping bar with a region switch (front-end only). The chosen region is kept in sessionStorage and
 * re-applied whenever Horizon re-renders the cart (the server always renders the first region).
 */
const REGION_KEY = 'dropit:ship-region';
const money = (cents) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/ /g, ' ');

/** Free shipping state seen last in this page view: confetti only when it turns on after a cart change */
const shipSeen = { done: /** @type {boolean | null} */ (null), total: 0 };

/**
 * Brand confetti (azul and prata) bursting from an element, drawn on a canvas inside the open dialog
 * so it shows above the drawer (top layer). Skipped with reduced motion.
 * @param {Element} from
 */
function confetti(from) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const host = from.closest('dialog[open]') || document.body;
  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483647';
  host.append(canvas);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = innerWidth * dpr;
  canvas.height = innerHeight * dpr;
  const ctx = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
  ctx.scale(dpr, dpr);

  const box = from.getBoundingClientRect();
  const colors = ['#0601f9', '#0601f9', '#c0c0c0', '#6d6bff'];
  const bits = Array.from({ length: 90 }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
    const speed = 380 + Math.random() * 420;
    return {
      x: box.left + Math.random() * box.width,
      y: box.top + box.height / 2,
      vx: Math.cos(angle) * speed * 0.7,
      vy: Math.sin(angle) * speed,
      w: 5 + Math.random() * 5,
      h: 8 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 14,
      color: colors[Math.floor(Math.random() * colors.length)],
      round: Math.random() < 0.3,
    };
  });

  const start = performance.now();
  let last = start;
  /** @param {number} now */
  const frame = (now) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    const age = (now - start) / 1000;
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    ctx.globalAlpha = Math.max(0, 1 - Math.max(0, age - 1.2) / 0.6);
    for (const b of bits) {
      b.vy += 900 * dt;
      b.vx *= 0.985;
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.rot += b.spin * dt;
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.fillStyle = b.color;
      if (b.round) {
        ctx.beginPath();
        ctx.arc(0, 0, b.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h * Math.abs(Math.cos(b.rot)));
      }
      ctx.restore();
    }
    if (age < 1.8) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}


class DropitShip extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onOutside);
    this.observer = new MutationObserver(() => this.apply());
    this.apply();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('click', this.#onOutside);
  }

  get menu() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-menu]'));
  }

  /** @param {boolean} open */
  toggle(open) {
    const menu = this.menu;
    const trigger = this.querySelector('[data-ship-toggle]');
    if (!menu || !trigger) return;
    menu.hidden = !open;
    trigger.setAttribute('aria-expanded', String(open));
    if (open) /** @type {HTMLElement | null} */ (menu.querySelector('[aria-selected="true"]') || menu.querySelector('button'))?.focus();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest('[data-ship-toggle]')) {
      this.toggle(!!this.menu?.hidden);
      return;
    }
    const option = /** @type {HTMLElement | null} */ (target.closest('[data-region]'));
    if (!option) return;
    const name = option.dataset.region || '';
    try {
      sessionStorage.setItem(REGION_KEY, name);
    } catch (error) {}
    document.querySelectorAll('dropit-ship').forEach((el) => {
      /** @type {any} */ (el).chosen = name;
      /** @type {any} */ (el).apply?.();
    });
    this.toggle(false);
    /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-toggle]'))?.focus();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.menu && !this.menu.hidden) {
      event.stopPropagation();
      this.toggle(false);
      /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-toggle]'))?.focus();
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && this.menu && !this.menu.hidden) {
      event.preventDefault();
      const items = /** @type {HTMLElement[]} */ ([...this.menu.querySelectorAll('button')]);
      const i = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      const next = items[(i + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
      next?.focus();
    }
  };

  /** @param {MouseEvent} event */
  #onOutside = (event) => {
    if (this.menu && !this.menu.hidden && !this.contains(/** @type {Node} */ (event.target))) this.toggle(false);
  };

  apply() {
    this.observer?.disconnect();
    let name = this.chosen;
    if (!name) {
      try {
        name = sessionStorage.getItem(REGION_KEY) || undefined;
      } catch (error) {}
    }
    const options = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-region]')]);
    const option = options.find((o) => o.dataset.region === name) || options[0];

    if (option) {
      const cents = Number(option.dataset.cents);
      const total = Number(this.dataset.total);
      const remaining = cents - total;
      const text = this.querySelector('[data-ship-text] span');
      const label = this.querySelector('[data-ship-name]');
      const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-bar]'));

      if (text) {
        text.innerHTML =
          remaining > 0
            ? `Faltam <strong>${money(remaining)}</strong> para frete grátis`
            : '<strong>Frete grátis</strong> liberado';
      }
      if (label) label.textContent = option.dataset.region || '';
      if (bar) bar.style.width = `${Math.min(100, (total / cents) * 100)}%`;
      this.classList.toggle('dp-cart-ship--done', remaining <= 0);

      // Celebrate only when a cart change (higher total) unlocks free shipping in a visible bar
      const done = remaining <= 0;
      if (done && shipSeen.done === false && total > shipSeen.total) {
        // The drawer may still be opening: wait (up to ~2s) until the bar is on screen
        const target = bar || this;
        let tries = 0;
        const fire = () => {
          if (target.getBoundingClientRect().width > 0 && /** @type {HTMLElement} */ (target).offsetParent) {
            window.setTimeout(() => confetti(target), 250);
          } else if (tries++ < 20) window.setTimeout(fire, 100);
        };
        fire();
      }
      shipSeen.done = done;
      shipSeen.total = total;
      options.forEach((o) => o.setAttribute('aria-selected', String(o === option)));
    }

    this.observer?.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-total', 'class'] });
  }
}

if (!customElements.get('dropit-ship')) customElements.define('dropit-ship', DropitShip);

/* Evey attendee form (app modal, product buy-now and cart checkout): the app has no close button.
   Adds one in the azul bar; Esc and a click on the backdrop also close it. Evey hides with .visually-hidden. */
(() => {
  const SEL = '#evey-attendee-info-modal, #evey-attendee-info-product-modal';
  const close = (m) => m.classList.add('visually-hidden');
  const addButtons = () => {
    document.querySelectorAll(SEL).forEach((m) => {
      const win = m.querySelector('.modal-window');
      if (!win || win.querySelector('.dp-evey-close')) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dp-evey-close';
      b.setAttribute('aria-label', 'Fechar');
      b.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      b.addEventListener('click', () => close(m));
      win.appendChild(b);
      m.addEventListener('click', (e) => { if (e.target === m) close(m); });
    });
  };
  addButtons();
  new MutationObserver(addButtons).observe(document.body, { childList: true, subtree: false });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll(SEL).forEach((m) => { if (!m.classList.contains('visually-hidden')) close(m); });
  });
})();

/* "Leve junto" row: arrows scroll it one card at a time (the cart re-renders, so everything is delegated). */
(() => {
  const update = (box) => {
    const list = box?.querySelector('[data-dp-bump-list]');
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    box.toggleAttribute('data-overflow', max > 4);
    const [prev, next] = box.querySelectorAll('[data-dp-bump-step]');
    if (prev) prev.disabled = list.scrollLeft <= 4;
    if (next) next.disabled = list.scrollLeft >= max - 4;
  };
  const updateAll = () => document.querySelectorAll('[data-dp-bumps]').forEach(update);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-dp-bump-step]');
    if (!btn) return;
    const list = btn.closest('[data-dp-bumps]')?.querySelector('[data-dp-bump-list]');
    const card = list?.querySelector('li');
    if (!list || !card) return;
    const step = card.getBoundingClientRect().width + 8;
    const max = list.scrollWidth - list.clientWidth;
    const left = Math.max(0, Math.min(max, list.scrollLeft + Number(btn.dataset.dpBumpStep) * step));
    // Safari ignores a smooth programmatic scroll on a scroll-snap list (it snaps back): snap off while moving
    list.style.scrollSnapType = 'none';
    list.scrollTo({ left, behavior: 'smooth' });
    clearTimeout(list._dpSnap);
    list._dpSnap = setTimeout(() => {
      list.style.scrollSnapType = '';
      update(btn.closest('[data-dp-bumps]'));
    }, 600);
  });
  document.addEventListener('scroll', (e) => {
    if (e.target?.matches?.('[data-dp-bump-list]')) update(e.target.closest('[data-dp-bumps]'));
  }, true);
  window.addEventListener('resize', updateAll);
  let raf = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(updateAll);
  }).observe(document.body, { childList: true, subtree: true });
  updateAll();
})();
